// pptxExporter.js - Complete editable PPTX export with template backgrounds
import PptxGenJS from 'pptxgenjs';
import slideTemplates from '../canvas/slideTemplates.json';

// ===== HELPER FUNCTIONS =====

const pxToPoints = (pxString) => {
  const px = parseInt(pxString);
  return Math.round(px * 0.50);
};

const pxToInches = (px) => {
  return (px / 1920) * 10;
};

const getBulletType = (bulletStyle) => {
  const bulletMap = {
    '•': 'bullet',
    '→': 'arrow',
    '▸': 'arrow',
    '■': 'square',
    '◆': 'diamond',
    '◉': 'circle',
    '⬢': 'diamond',
    '⬥': 'diamond',
    '✦': 'star',
    '◈': 'diamond',
    '⚡': 'star',
    '◇': 'diamond',
    '⬣': 'diamond',
    '—': 'number',
    '●': 'bullet',
    '▶': 'arrow',
    '❖': 'diamond'
  };
  return bulletMap[bulletStyle] || 'bullet';
};

/**
 * Calculate better Y position for subheading
 */
const calculateSubheadingY = (template, slideContent) => {
  if (!template.heading || !slideContent.heading) {
    return pxToInches(template.subheading?.y || 200);
  }
  
  const headingFontSize = parseInt(template.heading.font);
  const estimatedLines = Math.ceil(slideContent.heading.length / 40);
  const headingHeight = (headingFontSize * 1.2 * estimatedLines) / 72;
  
  return pxToInches(template.heading.y) + headingHeight + 0.3;
};

// ===== EXPORT FUNCTIONS =====

/**
 * Export all slides to PPTX with editable text
 * 
 * @param {Array} processedSlides - Array of slide data with extractedContent
 * @param {Array} slideTemplateIds - Array of template IDs for each slide
 * @param {Array} images - Array of image data (optional)
 * @param {Array} renderedSlides - Pre-rendered canvas slides (optional, for backgrounds)
 * @returns {Promise<Object>} Export result
 */
export const exportAllSlidesToPPTX = async (processedSlides, slideTemplateIds, images = [], renderedSlides = null) => {
  if (!processedSlides || processedSlides.length === 0) {
    throw new Error('No slides provided');
  }

  
  const pptx = new PptxGenJS();

  pptx.defineLayout({ name: 'LAYOUT_16x9', width: 10, height: 5.625 });
  pptx.layout = 'LAYOUT_16x9';

  pptx.author = 'Video Slide Generator';
  pptx.company = 'AI Slide Generator';
  pptx.title = 'Generated Presentation';

  for (let index = 0; index < processedSlides.length; index++) {
    const slideData = processedSlides[index];
    const templateId = slideTemplateIds[index] || 'modern_bullets';
    const template = slideTemplates[templateId];
    const slideContent = slideData.extractedContent;

    if (!template || !slideContent) {
   
      continue;
    }

    const slide = pptx.addSlide();

    // ✅ Check if this is a "none" layout template (no image)
    const isNoneLayout = templateId.includes('_none');

    // STEP 1: Add rendered canvas as background
    if (renderedSlides && renderedSlides[index]?.imageData) {
      slide.addImage({
        data: renderedSlides[index].imageData,
        x: 0,
        y: 0,
        w: '100%',
        h: '100%',
        sizing: {
          type: 'cover',
          w: '100%',
          h: '100%'
        },
        locked: true
      });
      
    } else {
      const bgColor = template.background?.color || '#FFFFFF';
      slide.background = { color: bgColor.replace('#', '') };
     
    }

    // ✅ STEP 2: SKIP text layers if this is a chart slide
    const isChartSlide = renderedSlides?.[index]?.isChartSlide === true;
    
    if (isChartSlide) {
     
      continue;  // ✅ Skip all text rendering for chart slides
    }

    // STEP 2: Add EDITABLE text layers on top (only for non-chart slides)
    
    // Add heading (editable)
    if (template.heading && slideContent.heading) {
      slide.addText(slideContent.heading, {
        x: isNoneLayout ? 0.5 : pxToInches(template.heading.x),
        y: pxToInches(template.heading.y),
        w: isNoneLayout ? 9 : pxToInches(template.heading.maxWidth),
        h: 1.5,
        fontSize: pxToPoints(template.heading.font),
        bold: true,
        color: template.heading.color.replace('#', ''),
        align: isNoneLayout ? 'center' : (template.heading.align || 'left'),
        valign: 'top',
        breakLine: true
      });
    }

    // Add subheading (editable)
    if (template.subheading && slideContent.subheading) {
      const subY = calculateSubheadingY(template, slideContent);
      
      slide.addText(slideContent.subheading, {
        x: isNoneLayout ? 0.5 : pxToInches(template.subheading.x),
        y: subY,
        w: isNoneLayout ? 9 : pxToInches(template.subheading.maxWidth),
        h: 1.2,
        fontSize: pxToPoints(template.subheading.font),
        color: template.subheading.color.replace('#', ''),
        align: isNoneLayout ? 'center' : (template.subheading.align || 'left'),
        valign: 'top',
        breakLine: true
      });
    }

    // Add bullet points (editable)
    if (template.bulletPoints && slideContent.bulletPoints && slideContent.bulletPoints.length > 0) {
      const bulletY = isNoneLayout ? 1.8 : 1.5;
      
      slide.addText(
        slideContent.bulletPoints.join('\n'),
        {
          x: isNoneLayout ? 2.9 : pxToInches(template.bulletPoints.x),
          y: bulletY,
          w: isNoneLayout ? 6 : pxToInches(template.bulletPoints.maxWidth),
          h: 4,
          fontSize: pxToPoints(template.bulletPoints.font),
          color: template.bulletPoints.color.replace('#', ''),
          bullet: { 
            type: getBulletType(template.bulletPoints.bulletStyle), 
            color: template.bulletPoints.bulletColor.replace('#', ''),
            indent: 20
          },
          align: 'left',
          valign: 'top',
          breakLine: true,
          lineSpacing: 32
        }
      );
    }
    
    
  }

  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
  const filename = `presentation-${processedSlides.length}slides-${timestamp}.pptx`;
  
  await pptx.writeFile({ fileName: filename });
  

  
  return { 
    success: true, 
    filename, 
    slideCount: processedSlides.length 
  };
};

/**
 * Export single slide to PPTX with editable text
 * 
 * @param {Object} slideContent - Slide content (heading, bullets, etc)
 * @param {string} templateId - Template ID to use
 * @param {number} slideIndex - Slide index for filename
 * @param {string} imageUrl - Image URL (not used in this version, kept for compatibility)
 * @param {Object} renderedSlideData - Pre-rendered canvas data
 * @returns {Promise<Object>} Export result
 */
export const exportSlideToPPTX = async (slideContent, templateId = 'modern_bullets', slideIndex = 0, imageUrl = null, renderedSlideData = null) => {
  const pptx = new PptxGenJS();
  const template = slideTemplates[templateId];
  
  if (!template) {
    throw new Error(`Template ${templateId} not found`);
  }



  pptx.defineLayout({ name: 'LAYOUT_16x9', width: 10, height: 5.625 });
  pptx.layout = 'LAYOUT_16x9';

  const slide = pptx.addSlide();

  // ✅ Check if this is a "none" layout template
  const isNoneLayout = templateId.includes('_none');

  // Add background image (rendered canvas)
  if (renderedSlideData?.imageData) {
    slide.addImage({
      data: renderedSlideData.imageData,
      x: 0,
      y: 0,
      w: '100%',
      h: '100%',
      sizing: {
        type: 'cover',
        w: '100%',
        h: '100%'
      },
      locked: true
    });
    
  } else {
    // Fallback: use template background color
    const bgColor = template.background?.color || '#FFFFFF';
    slide.background = { color: bgColor.replace('#', '') };
  
  }

  // Add editable text layers
  
  // Heading
  if (template.heading && slideContent.heading) {
    slide.addText(slideContent.heading, {
      x: isNoneLayout ? 0.5 : pxToInches(template.heading.x),
      y: pxToInches(template.heading.y),
      w: isNoneLayout ? 9 : pxToInches(template.heading.maxWidth),
      h: 1.5,
      fontSize: pxToPoints(template.heading.font),
      bold: true,
      color: template.heading.color.replace('#', ''),
      align: isNoneLayout ? 'center' : (template.heading.align || 'left'),
      valign: 'top',
      breakLine: true
    });
  }

  // Subheading
  if (template.subheading && slideContent.subheading) {
    const subY = calculateSubheadingY(template, slideContent);
    
    slide.addText(slideContent.subheading, {
      x: isNoneLayout ? 0.5 : pxToInches(template.subheading.x),
      y: subY,
      w: isNoneLayout ? 9 : pxToInches(template.subheading.maxWidth),
      h: 1.2,
      fontSize: pxToPoints(template.subheading.font),
      color: template.subheading.color.replace('#', ''),
      align: isNoneLayout ? 'center' : (template.subheading.align || 'left'),
      valign: 'top',
      breakLine: true
    });
  }

  // Bullet points
  if (template.bulletPoints && slideContent.bulletPoints && slideContent.bulletPoints.length > 0) {
    const bulletY = isNoneLayout ? 1.8 : 1.5;
    
    slide.addText(
      slideContent.bulletPoints.join('\n'),
      {
        x: isNoneLayout ? 2.9 : pxToInches(template.bulletPoints.x),
        y: bulletY,
        w: isNoneLayout ? 6 : pxToInches(template.bulletPoints.maxWidth),
        h: 4,
        fontSize: pxToPoints(template.bulletPoints.font),
        color: template.bulletPoints.color.replace('#', ''),
        bullet: { 
          type: getBulletType(template.bulletPoints.bulletStyle), 
          color: template.bulletPoints.bulletColor.replace('#', ''),
          indent: 20
        },
        align: 'left',
        valign: 'top',
        breakLine: true,
        lineSpacing: 32
      }
    );
  }

  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
  const filename = `slide-${slideIndex + 1}-${templateId}-${timestamp}.pptx`;
  
  await pptx.writeFile({ fileName: filename });
  

  
  return { 
    success: true, 
    filename 
  };
};